// src/modules/home/components/slots/PipelineSlot.jsx
// ─────────────────────────────────────────────────────────────────────────────
// PIPELINE SLOT — Save, load, replay DAG pipelines
//
// Shows saved pipelines and allows replay on new datasets.
// Pipelines are sequences of replayable actions saved from the live DAG.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import { T } from '../TopBar';
import {
  loadPipelines, deletePipeline, replayPipeline, formatRelTime, getUserKey,
} from '../../../utils/actionStore';

const MONO = 'JetBrains Mono,monospace';

// ── Single pipeline card ────────────────────────────────────────────────────
function PipelineCard({ pipeline, color, onReplay, onDelete, replayStatus }) {
  const [expanded, setExpanded] = useState(false);
  const isReplaying = replayStatus?.pipelineId === pipeline.id;

  return (
    <div style={{
      background: T.void, border: `1px solid ${T.b1}`,
      borderRadius: 3, padding: '8px 10px', marginBottom: 6,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
        <div
          style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
          onClick={() => setExpanded(e => !e)}
        >
          <div style={{
            fontSize: 10, color: T.t1, fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{pipeline.name}</div>
          <div style={{ fontSize: 8, color: T.t4, fontFamily: MONO }}>
            {pipeline.steps.length} steps · {formatRelTime(pipeline.updatedAt || pipeline.createdAt)}
          </div>
        </div>
        <button
          onClick={() => onReplay(pipeline)}
          disabled={isReplaying}
          style={{
            padding: '4px 6px', fontSize: 8,
            border: `1px solid ${T.green}44`,
            background: `${T.green}18`, color: T.green,
            borderRadius: 2, cursor: isReplaying ? 'default' : 'pointer',
            fontFamily: MONO, opacity: isReplaying ? 0.5 : 1,
          }}
        >{isReplaying ? '...' : 'REPLAY'}</button>
        <button
          onClick={() => onDelete(pipeline.id)}
          style={{
            padding: '4px 6px', fontSize: 8,
            border: `1px solid ${T.red}44`,
            background: `${T.red}10`, color: T.red,
            borderRadius: 2, cursor: 'pointer', fontFamily: MONO,
          }}
        >DEL</button>
      </div>

      {pipeline.desc && (
        <div style={{ fontSize: 8, color: T.t3, marginTop: 4 }}>{pipeline.desc}</div>
      )}

      {/* Expanded: show steps */}
      {expanded && (
        <div style={{
          marginTop: 6, padding: '6px 8px',
          background: T.deep, border: `1px solid ${T.b0}`,
          borderRadius: 2,
        }}>
          {pipeline.steps.map((step, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '3px 0',
              borderBottom: i < pipeline.steps.length - 1 ? `1px solid ${T.b0}` : 'none',
            }}>
              <span style={{ fontSize: 8, color: T.t4, fontFamily: MONO, width: 14, textAlign: 'right' }}>
                {i + 1}.
              </span>
              <span style={{ fontSize: 9, color: T.t2 }}>{step.label}</span>
              {Object.keys(step.params || {}).length > 0 && (
                <span style={{ fontSize: 7.5, color, fontFamily: MONO, marginLeft: 'auto' }}>
                  {Object.entries(step.params).slice(0, 2).map(([k, v]) => `${k}=${String(v).slice(0, 12)}`).join(' ')}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Replay status */}
      {isReplaying && replayStatus.label && (
        <div style={{
          marginTop: 4, fontSize: 8,
          color: replayStatus.status === 'done' ? T.green
            : replayStatus.status === 'error' ? T.red
            : T.cyan,
          fontFamily: MONO,
        }}>
          ▸ {replayStatus.label}
        </div>
      )}
    </div>
  );
}

// ── MAIN PIPELINE SLOT ──────────────────────────────────────────────────────
export default function PipelineSlot({ activeModule, authUser, color }) {
  const userKey = getUserKey(authUser);
  const [pipelines, setPipelines] = useState(() => loadPipelines(userKey));
  const [replayStatus, setReplayStatus] = useState(null);

  // Refresh when DAG updates (pipeline saved from DAG viewer)
  useEffect(() => {
    const handler = () => setPipelines(loadPipelines(userKey));
    window.addEventListener('dd:dag:updated', handler);
    return () => window.removeEventListener('dd:dag:updated', handler);
  }, [userKey]);

  const handleReplay = useCallback((pipeline) => {
    setReplayStatus({ pipelineId: pipeline.id, status: 'running', label: 'Starting...' });
    replayPipeline(
      pipeline,
      null,
      (p) => setReplayStatus({ pipelineId: pipeline.id, status: 'running', label: p.label }),
      (done) => {
        setReplayStatus({
          pipelineId: pipeline.id,
          status: done.success ? 'done' : 'error',
          label: done.success ? 'Complete' : 'Failed',
        });
        // Clear status after 3s
        setTimeout(() => setReplayStatus(null), 3000);
      }
    );
  }, []);

  const handleDelete = useCallback((id) => {
    deletePipeline(userKey, id);
    setPipelines(loadPipelines(userKey));
  }, [userKey]);

  // Filter pipelines for current module + show global ones
  const modulePipelines = pipelines.filter(p => p.module === activeModule || !p.module);

  return (
    <div>
      <div style={{
        fontSize: 7.5, color: T.t4, fontFamily: MONO, marginBottom: 6, letterSpacing: '.08em',
        display: 'flex', justifyContent: 'space-between',
      }}>
        <span>SAVED DAGS ({modulePipelines.length})</span>
      </div>

      {modulePipelines.length === 0 && (
        <div style={{ fontSize: 9, color: T.t3, lineHeight: 1.6 }}>
          No DAGs saved yet. Click "SAVE DAG" on a run in the live view to create one.
        </div>
      )}

      {modulePipelines.map(p => (
        <PipelineCard
          key={p.id}
          pipeline={p}
          color={color}
          onReplay={handleReplay}
          onDelete={handleDelete}
          replayStatus={replayStatus}
        />
      ))}
    </div>
  );
}
